import { formatDate, formatDateTime, formatMoney, pl, t } from "@hakili/i18n";
import { GuardedButton } from "@hakili/ui";
import { ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";

import type { Partner } from "./dealerships.js";
import { FicheConcession, type FicheConcessionProps } from "./fiche-concession.js";
import { BankCriteriaBlock, type CriteriaInput } from "./bank-criteria.js";
import { BankPanelBlock } from "./bank-panel.js";
import type { BankView } from "./partner-banks.js";
import { accountLine, partnerStatus, DASH, fill, StatusChip } from "./partner-cards.js";

/**
 * `round CN-B`, `CN-B.2`: **the partner's fiche, WF-CON-36, to the drawing
 * (`hakili-console.jsx`, `PartenaireScreen`) , live where a module answers,
 * in words where none does, a dash for a figure nobody sent.**
 *
 * 1. **Convention** , `lending/api.getConvention` answers `null` today
 *    (`partnerships.partner_conventions` is not built), so the block carries
 *    the drawing's own sentence for a partner with nothing signed and names
 *    the round.
 * 2. **Santé du partenariat** , per type, from the modules that answer:
 *    a bank's submitted, returns, decided, offers and refusals from
 *    `lending.submissions`; a dealer's controls from `expertise` are the
 *    cabinet's, not the seller's, so a dash; the prepaid balance is `payments`'
 *    and unanswered, a dash.
 * 3. **Motifs de refus** , in words: the pre-filter loop is WF-CON-37, `CN-C`.
 * 4. **Journal des échanges** , live: `GET`/`POST /partners/{id}/journal`, the
 *    four most recent, « Voir tout l'historique (N) », the composer in place.
 *    Only human exchanges are counted as exchanges.
 * 5. **Actions** , in words, the shared agenda is « Priorités »'s, `CN-C`.
 * 6. **Contacts** , from identity: the organisation's first responsible and
 *    the state of his account. For a bank, **the criteria** and **its own
 *    frais de dossier** (the founder's decision of 11 September) in words,
 *    the lending round named.
 *
 * A concession's fiche keeps `FicheConcession` , the administrator's naming,
 * the address , below the drawing's blocks.
 */

export interface JournalEntryView {
  readonly id: string;
  readonly type: string;
  readonly body: string;
  readonly occurredAt: string;
  readonly authorLabel: string;
  readonly origin: string | null;
  readonly isHuman: boolean;
}

export interface PartnerSheetDoors {
  readonly readJournal: (
    organisationId: string,
  ) => Promise<{ entries: readonly JournalEntryView[]; humanExchanges: number }>;
  readonly logExchange: (organisationId: string, type: string, body: string) => Promise<void>;
  readonly readBank: (organisationId: string) => Promise<BankView | null>;
  /**
   * `T-1d`: the dealer's prepaid balance, the ledger's sum by `payments`' port through the
   * account door; `null` while unanswered, and the tile keeps its dash (R81).
   */
  /**
   * `FX-3`, `M4 (b)`: the dealer's health facts the account door answers , the ledger's sum (`T-1d`) and
   * « Véhicules en ligne », which was a hard dash on this page while the dealership had one online.
   */
  readonly readDealerHealth?: (organisationId: string) => Promise<{
    readonly prepaid: { balance: number; low: boolean; threshold: number | null } | null;
    readonly onlineVehicles: number | null;
  }>;
  /**
   * `round K-2` (resumed), `M4` (WF-CON-36 amended): the dealer's breaches with their claims and
   * the sanctions' state, all derived by the door; the two acts under row 24 where the caller
   * holds it (`mayActOnClaims`), greyed with the reason otherwise.
   */
  readonly readBreaches?: (organisationId: string) => Promise<BreachSheetView>;
  readonly settleClaim?: (organisationId: string, claimId: string) => Promise<void>;
  readonly waiveClaim?: (organisationId: string, claimId: string, note: string) => Promise<void>;
  /** `CN-C.5`, WF-CON-37 step 2: `PUT /banks/{id}/prefilter`, the one state Priorités and the fiche read. */
  readonly adjustPrefilter?: (
    bankId: string,
    input: { readonly thresholdPct: number; readonly label: string },
  ) => Promise<void>;
  /** `OD-43`, `M1`: `PUT /banks/{id}/criteria`, row 14's right , the four figures the Hakili filter reads. */
  readonly recordCriteria?: (bankId: string, input: CriteriaInput) => Promise<void>;
  /** `PRET-BANKS`: `PUT /banks/{id}/panel`, row `ouvrirOrganisation` , admit a bank to the panel the filter walks, or take it off. */
  readonly recordPanel?: (bankId: string, active: boolean) => Promise<void>;
  /** `L-1`, `M2`: `PUT /banks/{id}/convention`, row 14's right , the convention and the founder's field. */
  readonly recordConvention?: (bankId: string, input: ConventionInput) => Promise<void>;
  /**
   * `T-1c`, `M3` (I31 amended): a dealership's convention , its negotiated commission term
   * against the standard the door answers beside it , `GET`/`PUT /partners/{orgId}/convention`.
   * The pen is the relation seat's for its own portfolio and the administration's (M0 (c)).
   */
  readonly readDealerConvention?: (organisationId: string) => Promise<DealerConventionView>;
  readonly recordDealerConvention?: (
    organisationId: string,
    input: {
      readonly dealerCommissionPctNegotiated: number | null;
      readonly negotiatedNote: string;
      /** Absent: the door keeps the type it holds. */
      readonly conventionType?: ConventionKind;
    },
  ) => Promise<void>;
}

export interface DealerConventionView {
  readonly convention: {
    readonly negotiatedNote: string;
    readonly dealerCommissionPctNegotiated: number | null;
    /** Served by `GET /partners/{id}/convention` (the dealer's door answers the whole row). */
    readonly conventionType?: ConventionKind | null;
  } | null;
  readonly standardRatePct: number | null;
}

/**
 * `CONVENTION-TYPE`: the convention's type, **the door's own enum** (`ConventionType` in `partnerships/api`,
 * `CONVENTION_TYPES` in `bank-convention.controller.ts`): « pilote » or « contratComplet ». The first-admin door
 * refuses a bank or a dealership whose convention has none.
 */
export type ConventionKind = "pilote" | "contratComplet";

const CONVENTION_KINDS: readonly (readonly [
  ConventionKind,
  "console.partenaires.conventionTypePilote" | "console.partenaires.conventionTypeComplet",
])[] = [
  ["pilote", "console.partenaires.conventionTypePilote"],
  ["contratComplet", "console.partenaires.conventionTypeComplet"],
];

export interface ConventionInput {
  readonly signedOn: string | null;
  readonly expiresOn: string | null;
  readonly slaDays: number | null;
  readonly negotiatedNote: string;
  readonly fileFeePct: number | null;
  /** The door replaces the whole convention on each save, so a save without a type would erase the one it holds. */
  readonly conventionType: ConventionKind;
}

export interface BreachClaimView {
  readonly id: string;
  readonly kind: "remboursement" | "penalite";
  readonly amount: number;
  readonly status: "ouverte" | "reglee" | "abandonnee";
  readonly settledMovementReference: string | null;
  readonly waiverNote: string | null;
}

export interface BreachRowView {
  readonly id: string;
  readonly vehicleLabel: string;
  readonly stockReference: string;
  readonly circumstance: string;
  readonly leftOn: string;
  readonly note: string | null;
  readonly evidenceDocumentId: string;
  readonly recordedAt: string;
  readonly claims: readonly BreachClaimView[];
}

export interface BreachSheetView {
  readonly breaches: readonly BreachRowView[];
  readonly sanctions: {
    readonly demotedUntil: string | null;
    readonly breachCount: number;
    readonly suspended: boolean;
  };
}

export interface PartnerSheetProps extends PartnerSheetDoors {
  readonly vue: string;
  readonly partner: Partner;
  readonly onBack: () => void;
  /** 06 §6.1.5 row 15: whether the caller may adjust a bank's pre-filter; the control is greyed otherwise. */
  readonly mayAdjustPrefilter?: boolean;
  /** 06 §6.1.5 row 14: whether the caller may record a bank's convention; the control is greyed otherwise. */
  readonly mayRecordConvention?: boolean;
  /** `OD-43`: row 14's right again , whether the caller may set a bank's four criteria; greyed otherwise. */
  readonly mayConfigureCriteria?: boolean;
  /** `PRET-BANKS`: `ouvrirOrganisation` , whether the caller may admit a bank to the panel; the control is not drawn otherwise. */
  readonly mayAdmitToPanel?: boolean;
  /** `T-1c`: row 11 (`animerPortefeuillePartenaires`); the portfolio itself is the door's to refuse. */
  readonly mayRecordDealerConvention?: boolean;
  /** `K-2` (resumed): row 24, the administration alone , the settlement and the waiver. */
  readonly mayActOnClaims?: boolean;
  /** The concession's own blocks, for a dealer. */
  readonly concession?: Omit<FicheConcessionProps, "concession" | "administrateur">;
}

const TYPE_WORDS = new Map<string, Parameters<typeof t>[0]>([
  ["banque", "console.partenaires.typeBanque"],
  ["concessionnaire", "console.partenaires.typeConcessionnaire"],
  ["cabinet", "console.partenaires.typeCabinet"],
  ["assureur", "console.partenaires.typeAssureur"],
]);
const CHANNEL_WORDS = new Map<string, Parameters<typeof t>[0]>([
  ["appel", "console.partenaires.canalAppel"],
  ["visite", "console.partenaires.canalVisite"],
  ["courriel", "console.partenaires.canalCourriel"],
  ["comite", "console.partenaires.canalComite"],
  ["whatsapp", "console.partenaires.canalWhatsapp"],
  ["systeme", "console.partenaires.canalSysteme"],
]);
const CHANNELS = ["appel", "visite", "courriel", "comite", "whatsapp"] as const;
const JOURNAL_CEILING = 4;

/** The circumstance's words, the catalogue's; a code the catalogue does not know is printed as sent. */
function circumstanceWords(code: string): string {
  const keys: Readonly<Record<string, Parameters<typeof t>[0]>> = {
    ["venduATiers"]: "console.partenaires.circonstanceVenduATiers",
    ["venduAAcheteurHakiliHorsPlateforme"]:
      "console.partenaires.circonstanceVenduAAcheteurHakiliHorsPlateforme",
    ["retireSansVente"]: "console.partenaires.circonstanceRetireSansVente",
    ["autre"]: "console.partenaires.circonstanceAutre",
  };
  const key = keys[code];
  return key === undefined ? code : t(key);
}

function Block({
  title,
  children,
  testId,
}: {
  readonly title: string;
  readonly children: React.ReactNode;
  readonly testId: string;
}): React.JSX.Element {
  return (
    <section data-testid={testId} className="rounded-2xl border border-gray-200 bg-white px-5 py-4">
      <h2 className="mb-2 text-sm font-semibold">{title}</h2>
      {children}
    </section>
  );
}

export function PartnerSheet(props: PartnerSheetProps): React.JSX.Element {
  const [journal, setJournal] = useState<{
    entries: readonly JournalEntryView[];
    humanExchanges: number;
  } | null>(null);
  const [bank, setBank] = useState<BankView | null | undefined>(undefined);
  const [prepaidBalance, setPrepaidBalance] = useState<{
    balance: number;
    low: boolean;
    threshold: number | null;
  } | null>(null);
  const [onlineVehicles, setOnlineVehicles] = useState<number | null>(null);
  const [breaches, setBreaches] = useState<BreachSheetView | null>(null);
  const [claimBusy, setClaimBusy] = useState<string | null>(null);
  const [claimRefusal, setClaimRefusal] = useState<string | null>(null);
  const [waiving, setWaiving] = useState<{ id: string; note: string } | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [channel, setChannel] = useState<string>("appel");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [epoch, setEpoch] = useState(0);
  const [prefilterOpen, setPrefilterOpen] = useState(false);
  const [threshold, setThreshold] = useState("");
  const [prefilterLabel, setPrefilterLabel] = useState("");
  const [conventionOpen, setConventionOpen] = useState(false);
  const [signedOn, setSignedOn] = useState("");
  const [expiresOn, setExpiresOn] = useState("");
  const [slaDays, setSlaDays] = useState("");
  const [negotiatedNote, setNegotiatedNote] = useState("");
  const [fileFeePct, setFileFeePct] = useState("");
  const [conventionType, setConventionType] = useState<ConventionKind | "">("");
  // `T-1c`: the dealer's convention as the door answers it, the term's form.
  const [dealerConvention, setDealerConvention] = useState<DealerConventionView | null>(null);
  const [termOpen, setTermOpen] = useState(false);
  const [termRate, setTermRate] = useState("");
  const [termNote, setTermNote] = useState("");
  const [termType, setTermType] = useState<ConventionKind | "">("");
  const { readJournal, readBank, readDealerConvention, readDealerHealth, readBreaches, partner } = props;
  useEffect(() => {
    if (partner.type !== "concessionnaire" || readDealerConvention === undefined) return undefined;
    const run = { live: true };
    void readDealerConvention(partner.id)
      .then((answered) => {
        if (run.live) setDealerConvention(answered);
      })
      .catch(() => {
        if (run.live) setDealerConvention(null);
      });
    return () => {
      run.live = false;
    };
  }, [readDealerConvention, partner, epoch]);

  useEffect(() => {
    const run = { live: true };
    void (async () => {
      try {
        const answered = await readJournal(partner.id);
        if (run.live) setJournal(answered);
      } catch (failure) {
        if (run.live)
          setRefusal((failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"));
      }
      if (partner.type === "banque") {
        try {
          const answered = await readBank(partner.id);
          if (run.live) setBank(answered);
        } catch {
          if (run.live) setBank(null);
        }
      }
      if (partner.type === "concessionnaire" && readDealerHealth !== undefined) {
        try {
          const answered = await readDealerHealth(partner.id);
          if (run.live) {
            setPrepaidBalance(answered.prepaid);
            setOnlineVehicles(answered.onlineVehicles);
          }
        } catch {
          if (run.live) {
            setPrepaidBalance(null);
            setOnlineVehicles(null);
          }
        }
      }
      if (partner.type === "concessionnaire" && readBreaches !== undefined) {
        try {
          const answered = await readBreaches(partner.id);
          if (run.live) setBreaches(answered);
        } catch {
          if (run.live) setBreaches(null);
        }
      }
    })();
    return () => {
      run.live = false;
    };
  }, [readJournal, readBank, readDealerHealth, readBreaches, partner, epoch]);

  /** `K-2` (resumed): one of the two acts on a claim, then the sheet re-read; the door's refusal in words. */
  const actOnClaim = (claimId: string, act: () => Promise<void>): void => {
    setClaimBusy(claimId);
    setClaimRefusal(null);
    void act()
      .then(() => {
        setWaiving(null);
        setEpoch((now) => now + 1);
      })
      .catch((failure: unknown) => {
        setClaimRefusal(
          (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"),
        );
      })
      .finally(() => {
        setClaimBusy(null);
      });
  };

  const typeKey = TYPE_WORDS.get(partner.type);
  const entries = journal?.entries ?? [];
  const shown = expanded ? entries : entries.slice(0, JOURNAL_CEILING);

  const health: [string, string][] =
    partner.type === "banque"
      ? [
          [
            t("console.partenaires.santeSoumis"),
            bank === undefined || bank === null ? DASH : String(bank.volumes.submitted),
          ],
          [
            t("console.partenaires.santeRetours"),
            bank === undefined || bank === null ? DASH : String(bank.volumes.returned),
          ],
          [
            t("console.partenaires.santeDecides"),
            bank === undefined || bank === null ? DASH : String(bank.volumes.decided),
          ],
          [
            t("console.partenaires.santeOffres"),
            bank === undefined || bank === null ? DASH : String(bank.volumes.offers),
          ],
          [
            t("console.partenaires.santeRefus"),
            bank === undefined || bank === null ? DASH : String(bank.volumes.refusals),
          ],
          [t("console.partenaires.santeDelai"), DASH],
        ]
      : partner.type === "concessionnaire"
        ? [
            // `FX-3`, `M4 (b)` (`S2-7`): the count, asked of `catalog` through the account door. A dash
            // stays where the door has not answered , which is a state and not a zero (`R81`).
            [
              t("console.partenaires.santeVehicules"),
              onlineVehicles === null ? DASH : String(onlineVehicles),
            ],
            [t("console.partenaires.santeControles"), DASH],
            [t("console.partenaires.santeNonConformes"), DASH],
            [t("console.partenaires.santeEcartPrix"), DASH],
            // `T-1d`: the ledger's sum, read; a dash only while the door has not answered.
            [
              t("console.partenaires.santeSolde"),
              prepaidBalance === null
                ? DASH
                : prepaidBalance.low && prepaidBalance.threshold !== null
                  ? `${formatMoney(prepaidBalance.balance)} · ${t("console.partenaires.soldeBas").replace("{seuil}", formatMoney(prepaidBalance.threshold))}`
                  : formatMoney(prepaidBalance.balance),
            ],
          ]
        : [];

  return (
    <div data-testid="partenaire-fiche" className="flex flex-col gap-4">
      <div>
        <button
          type="button"
          data-testid="partenaire-retour"
          onClick={props.onBack}
          className="mb-3 flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
          {t("console.partenaires.retourAnnuaire")}
        </button>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">{partner.name}</h1>
          <StatusChip status={partnerStatus(partner)} />
        </div>
        <p className="mt-1.5 text-sm text-gray-500">
          {typeKey === undefined ? partner.type : t(typeKey)}
          {props.vue === "admin" && partner.managerName !== null && partner.managerName !== undefined
            ? ` · ${fill("console.partenaires.suiviPar", { nom: partner.managerName })}`
            : ""}
        </p>
      </div>

      <Block title={t("console.partenaires.conventionTitre")} testId="fiche-convention">
        {/* `L-1`, `M2` (`P1-LND-046`): the convention as `partnerships` keeps it,
            read through the bank's own record; the founder's field beside the
            SLA. For a partner that is not a bank the block keeps its words. */}
        {partner.type === "banque" && bank?.convention !== undefined && bank.convention !== null ? (
          <div className="space-y-1 text-sm text-gray-700">
            <p data-testid="fiche-convention-signee">
              {bank.convention.signedOn === null
                ? t("console.partenaires.conventionNonSignee")
                : fill("console.partenaires.conventionSigneeLe", {
                    date: formatDate(new Date(bank.convention.signedOn)),
                    echeance:
                      bank.convention.expiresOn === null
                        ? DASH
                        : formatDate(new Date(bank.convention.expiresOn)),
                  })}
            </p>
            <p data-testid="fiche-convention-sla">
              {fill("console.partenaires.conventionSla", {
                jours: bank.convention.slaDays === null ? DASH : String(bank.convention.slaDays),
              })}
            </p>
            {bank.convention.negotiatedNote === "" ? null : (
              <p className="text-gray-500" data-testid="fiche-convention-negocie">
                {bank.convention.negotiatedNote}
              </p>
            )}
          </div>
        ) : partner.type === "concessionnaire" && dealerConvention !== null ? (
          // `T-1c`, `M3`: the dealer's commission term against the standard, both the door's figures.
          <div className="space-y-1 text-sm text-gray-700" data-testid="fiche-commission">
            {/* The term as the door answers it; `null` reads the standard. */}
            <p data-testid="fiche-commission-taux">
              {(dealerConvention.convention?.dealerCommissionPctNegotiated ?? null) === null
                ? fill("console.partenaires.commissionStandard", {
                    taux:
                      dealerConvention.standardRatePct === null
                        ? DASH
                        : String(dealerConvention.standardRatePct),
                  })
                : fill("console.partenaires.commissionNegociee", {
                    taux: String(dealerConvention.convention?.dealerCommissionPctNegotiated),
                    standard:
                      dealerConvention.standardRatePct === null
                        ? DASH
                        : String(dealerConvention.standardRatePct),
                  })}
            </p>
            {dealerConvention.convention === null ||
            dealerConvention.convention.negotiatedNote === "" ? null : (
              <p className="text-gray-500" data-testid="fiche-commission-note">
                {dealerConvention.convention.negotiatedNote}
              </p>
            )}
            <div className="mt-2">
              {props.mayRecordDealerConvention !== true || props.recordDealerConvention === undefined ? (
                <GuardedButton
                  disabled
                  reason={t("console.partenaires.commissionMotifDroit")}
                  variant="secondary"
                  testId="fiche-commission-modifier"
                >
                  {t("console.partenaires.commissionModifier")}
                </GuardedButton>
              ) : termOpen ? (
                <form
                  data-testid="fiche-commission-form"
                  className="grid grid-cols-2 gap-2 rounded-xl bg-gray-50 px-4 py-3"
                  onSubmit={(event) => {
                    event.preventDefault();
                    const door = props.recordDealerConvention;
                    if (door === undefined) return;
                    void (async () => {
                      setBusy(true);
                      setRefusal(null);
                      try {
                        await door(partner.id, {
                          dealerCommissionPctNegotiated: termRate.trim() === "" ? null : Number(termRate),
                          negotiatedNote: termNote.trim(),
                          ...(termType === "" ? {} : { conventionType: termType }),
                        });
                        setTermOpen(false);
                        setEpoch((now) => now + 1);
                      } catch (failure) {
                        setRefusal(
                          (failure as { message?: string } | null)?.message ??
                            t("common.error.connexionEchouee"),
                        );
                      } finally {
                        setBusy(false);
                      }
                    })();
                  }}
                >
                  <label className="text-xs text-gray-600">
                    {t("console.partenaires.commissionChampTaux")}
                    <input
                      data-testid="fiche-commission-taux-saisie"
                      inputMode="numeric"
                      value={termRate}
                      placeholder={
                        dealerConvention.standardRatePct === null
                          ? ""
                          : String(dealerConvention.standardRatePct)
                      }
                      onChange={(event) => {
                        setTermRate(event.target.value.replace(/\D/gu, ""));
                      }}
                      className="mt-1 block w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs"
                    />
                  </label>
                  <label className="text-xs text-gray-600">
                    {t("console.partenaires.commissionChampNote")}
                    <input
                      data-testid="fiche-commission-note-saisie"
                      value={termNote}
                      onChange={(event) => {
                        setTermNote(event.target.value);
                      }}
                      className="mt-1 block w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs"
                    />
                  </label>
                  <label className="col-span-2 text-xs text-gray-600">
                    {t("console.partenaires.conventionChampType")}
                    <select
                      data-testid="fiche-commission-type"
                      value={termType}
                      onChange={(event) => {
                        setTermType(event.target.value as ConventionKind | "");
                      }}
                      className="mt-1 block w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs"
                    >
                      <option value="" />
                      {CONVENTION_KINDS.map(([value, label]) => (
                        <option key={value} value={value}>
                          {t(label)}
                        </option>
                      ))}
                    </select>
                  </label>
                  {refusal === null ? null : (
                    <p
                      role="alert"
                      data-testid="fiche-commission-refus"
                      className="col-span-2 text-xs text-rose-600"
                    >
                      {refusal}
                    </p>
                  )}
                  <div className="col-span-2 flex gap-2">
                    <button
                      type="submit"
                      disabled={busy}
                      data-testid="fiche-commission-enregistrer"
                      className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-60"
                    >
                      {t("console.partenaires.conventionEnregistrer")}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setTermOpen(false);
                      }}
                      className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700"
                    >
                      {t("common.action.annuler")}
                    </button>
                  </div>
                </form>
              ) : (
                <button
                  type="button"
                  data-testid="fiche-commission-modifier"
                  onClick={() => {
                    setTermRate(String(dealerConvention.convention?.dealerCommissionPctNegotiated ?? ""));
                    setTermNote(dealerConvention.convention?.negotiatedNote ?? "");
                    setTermType(dealerConvention.convention?.conventionType ?? "");
                    setTermOpen(true);
                  }}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700"
                >
                  {t("console.partenaires.commissionModifier")}
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm leading-relaxed text-gray-600" data-testid="fiche-convention-aucune">
              {t("console.partenaires.conventionAucune")}
            </p>
          </>
        )}
        {partner.type === "banque" ? (
          <>
            <p className="mt-2 text-sm text-gray-900" data-testid="fiche-frais-dossier">
              {fill("console.partenaires.fraisDossierBanque", {
                taux:
                  bank?.convention?.fileFeePct === undefined || bank.convention.fileFeePct === null
                    ? t("console.partenaires.fraisDossierDefaut")
                    : fill("console.partenaires.fraisDossierTaux", {
                        taux: String(bank.convention.fileFeePct),
                      }),
              })}
            </p>
            <div className="mt-2">
              {bank === undefined ||
              bank === null ||
              props.mayRecordConvention !== true ||
              props.recordConvention === undefined ? (
                <GuardedButton
                  disabled
                  reason={t("console.partenaires.conventionMotifDroit")}
                  variant="secondary"
                  testId="fiche-convention-modifier"
                >
                  {t("console.partenaires.conventionModifier")}
                </GuardedButton>
              ) : conventionOpen ? (
                <form
                  data-testid="fiche-convention-form"
                  className="grid grid-cols-2 gap-2 rounded-xl bg-gray-50 px-4 py-3"
                  onSubmit={(event) => {
                    event.preventDefault();
                    const door = props.recordConvention;
                    if (door === undefined || conventionType === "") return;
                    void (async () => {
                      setBusy(true);
                      setRefusal(null);
                      try {
                        await door(bank.id, {
                          signedOn: signedOn.trim() === "" ? null : signedOn.trim(),
                          expiresOn: expiresOn.trim() === "" ? null : expiresOn.trim(),
                          slaDays: slaDays.trim() === "" ? null : Number(slaDays),
                          negotiatedNote: negotiatedNote.trim(),
                          fileFeePct: fileFeePct.trim() === "" ? null : Number(fileFeePct),
                          conventionType,
                        });
                        setConventionOpen(false);
                        setEpoch((now) => now + 1);
                      } catch (failure) {
                        setRefusal(
                          (failure as { message?: string } | null)?.message ??
                            t("common.error.connexionEchouee"),
                        );
                      } finally {
                        setBusy(false);
                      }
                    })();
                  }}
                >
                  <label className="text-xs text-gray-600">
                    {t("console.partenaires.conventionChampSignee")}
                    <input
                      data-testid="fiche-convention-signee-le"
                      type="date"
                      value={signedOn}
                      onChange={(event) => {
                        setSignedOn(event.target.value);
                      }}
                      className="mt-1 block w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs"
                    />
                  </label>
                  <label className="text-xs text-gray-600">
                    {t("console.partenaires.conventionChampEcheance")}
                    <input
                      data-testid="fiche-convention-echeance"
                      type="date"
                      value={expiresOn}
                      onChange={(event) => {
                        setExpiresOn(event.target.value);
                      }}
                      className="mt-1 block w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs"
                    />
                  </label>
                  <label className="text-xs text-gray-600">
                    {t("console.partenaires.conventionChampSla")}
                    <input
                      data-testid="fiche-convention-sla-jours"
                      inputMode="numeric"
                      value={slaDays}
                      onChange={(event) => {
                        setSlaDays(event.target.value);
                      }}
                      className="mt-1 block w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs"
                    />
                  </label>
                  <label className="text-xs text-gray-600">
                    {t("console.partenaires.conventionChampFrais")}
                    <input
                      data-testid="fiche-convention-frais"
                      inputMode="numeric"
                      value={fileFeePct}
                      placeholder={t("console.partenaires.fraisDossierDefaut")}
                      onChange={(event) => {
                        setFileFeePct(event.target.value);
                      }}
                      className="mt-1 block w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs"
                    />
                  </label>
                  <label className="text-xs text-gray-600">
                    {t("console.partenaires.conventionChampType")}
                    <select
                      data-testid="fiche-convention-type"
                      required
                      value={conventionType}
                      onChange={(event) => {
                        setConventionType(event.target.value as ConventionKind | "");
                      }}
                      className="mt-1 block w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs"
                    >
                      <option value="" />
                      {CONVENTION_KINDS.map(([value, label]) => (
                        <option key={value} value={value}>
                          {t(label)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="col-span-2 text-xs text-gray-600">
                    {t("console.partenaires.conventionChampNote")}
                    <input
                      data-testid="fiche-convention-note"
                      value={negotiatedNote}
                      onChange={(event) => {
                        setNegotiatedNote(event.target.value);
                      }}
                      className="mt-1 block w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs"
                    />
                  </label>
                  <div className="col-span-2">
                    <button
                      type="submit"
                      data-testid="fiche-convention-enregistrer"
                      disabled={busy}
                      className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
                    >
                      {t("console.partenaires.conventionEnregistrer")}
                    </button>
                  </div>
                </form>
              ) : (
                <button
                  type="button"
                  data-testid="fiche-convention-modifier"
                  onClick={() => {
                    const standing = bank.convention ?? null;
                    setSignedOn(standing?.signedOn ?? "");
                    setExpiresOn(standing?.expiresOn ?? "");
                    setSlaDays(
                      standing?.slaDays === null || standing === null ? "" : String(standing.slaDays),
                    );
                    setNegotiatedNote(standing?.negotiatedNote ?? "");
                    setFileFeePct(
                      standing?.fileFeePct === null || standing === null ? "" : String(standing.fileFeePct),
                    );
                    setConventionType("");
                    setConventionOpen(true);
                  }}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  {t("console.partenaires.conventionModifier")}
                </button>
              )}
            </div>
          </>
        ) : null}
      </Block>

      {health.length === 0 ? null : (
        <Block title={t("console.partenaires.santeTitre")} testId="fiche-sante">
          <div className="grid grid-cols-3 gap-3">
            {health.map(([label, value]) => (
              <div
                key={label}
                className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3"
                // `FX-3`, `M4 (b)`: the vehicles tile is named too, so a spec can read the count rather than
                // the whole card's text , the dash and a one are both short enough to match by accident.
                data-testid={
                  label === t("console.partenaires.santeSolde")
                    ? "fiche-sante-solde"
                    : label === t("console.partenaires.santeVehicules")
                      ? "fiche-sante-vehicules"
                      : undefined
                }
              >
                <p className="text-xs text-gray-500">{label}</p>
                <p className="mt-0.5 text-sm font-medium tabular-nums">{value}</p>
              </div>
            ))}
          </div>
          <p className="mt-2 text-sm text-gray-500">{t("console.partenaires.santeNote")}</p>
        </Block>
      )}

      {partner.type === "concessionnaire" && readBreaches !== undefined ? (
        <Block title={t("console.partenaires.manquementsTitre")} testId="fiche-manquements">
          {/* `K-2` (resumed), `M4`, WF-CON-36 amended: one row per breach with its claims and
              their derived states; the two acts under row 24; the sanctions' state, derived. */}
          {breaches === null ? (
            <p className="text-xs text-gray-500">{t("common.state.chargement")}</p>
          ) : (
            <>
              <p data-testid="fiche-manquements-compteur" className="mb-2 text-sm text-gray-500">
                {breaches.breaches.length === 0
                  ? t("console.partenaires.manquementsVide")
                  : pl(
                      breaches.sanctions.breachCount,
                      t("console.partenaires.manquementUn"),
                      t("console.partenaires.manquementPlusieurs"),
                    )}
              </p>
              <p data-testid="fiche-sanctions" className="mb-3 text-sm text-gray-600">
                {breaches.sanctions.suspended
                  ? fill("console.partenaires.sanctionSuspendu", {
                      n: String(breaches.sanctions.breachCount),
                    })
                  : breaches.sanctions.demotedUntil === null
                    ? t("console.partenaires.sanctionAucune")
                    : fill("console.partenaires.sanctionRetrogradation", {
                        date: formatDate(new Date(breaches.sanctions.demotedUntil)),
                      })}
              </p>
              <ul className="space-y-3">
                {breaches.breaches.map((row) => (
                  <li
                    key={row.id}
                    data-testid={`manquement-${row.id}`}
                    className="rounded-xl border border-gray-100 px-4 py-3"
                  >
                    <p className="text-sm font-medium text-gray-900">
                      {fill("console.partenaires.manquementLigne", {
                        vehicule: `${row.vehicleLabel} (${row.stockReference})`,
                        circonstance: circumstanceWords(row.circumstance),
                        date: formatDate(new Date(row.leftOn)),
                      })}
                    </p>
                    <p className="mt-0.5 text-sm text-gray-500">
                      {fill("console.partenaires.manquementConstate", {
                        date: formatDate(new Date(row.recordedAt)),
                      })}
                      {" · "}
                      {t("console.partenaires.manquementPreuve")}
                      {row.note === null
                        ? ""
                        : ` · ${fill("console.partenaires.manquementNote", { note: row.note })}`}
                    </p>
                    <ul className="mt-2 space-y-1.5">
                      {row.claims.map((claim) => (
                        <li
                          key={claim.id}
                          data-testid={`creance-${claim.id}`}
                          className="flex flex-wrap items-center gap-2 text-xs"
                        >
                          <span className="text-gray-700">
                            {claim.kind === "remboursement"
                              ? t("console.partenaires.creanceRemboursement")
                              : t("console.partenaires.creancePenalite")}
                          </span>
                          <span className="font-medium tabular-nums text-gray-900">
                            {formatMoney(claim.amount)}
                          </span>
                          <span
                            data-testid="creance-etat"
                            className={
                              "rounded-full px-2 py-0.5 " +
                              (claim.status === "reglee"
                                ? "bg-emerald-50 text-emerald-700"
                                : claim.status === "abandonnee"
                                  ? "bg-gray-100 text-gray-600"
                                  : "bg-amber-50 text-amber-700")
                            }
                          >
                            {claim.status === "reglee"
                              ? fill("console.partenaires.creanceReglee", {
                                  reference: claim.settledMovementReference ?? DASH,
                                })
                              : claim.status === "abandonnee"
                                ? fill("console.partenaires.creanceAbandonnee", {
                                    note: claim.waiverNote ?? "",
                                  })
                                : t("console.partenaires.creanceOuverte")}
                          </span>
                          {claim.status !== "ouverte" ? null : props.mayActOnClaims === true &&
                            props.settleClaim !== undefined &&
                            props.waiveClaim !== undefined ? (
                            <>
                              <button
                                type="button"
                                data-testid="creance-regler"
                                disabled={claimBusy !== null}
                                onClick={() => {
                                  const settle = props.settleClaim;
                                  if (settle !== undefined)
                                    actOnClaim(claim.id, () => settle(partner.id, claim.id));
                                }}
                                className="rounded-lg bg-indigo-600 px-2.5 py-1 text-xs font-medium text-white disabled:opacity-60"
                              >
                                {t("console.partenaires.creanceRegler")}
                              </button>
                              <button
                                type="button"
                                data-testid="creance-renoncer"
                                disabled={claimBusy !== null}
                                onClick={() => {
                                  setWaiving({ id: claim.id, note: "" });
                                }}
                                className="rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700"
                              >
                                {t("console.partenaires.creanceRenoncer")}
                              </button>
                            </>
                          ) : (
                            <GuardedButton
                              disabled
                              reason={t("console.partenaires.creanceAdministrationSeule")}
                              variant="secondary"
                              testId="creance-regler"
                            >
                              {t("console.partenaires.creanceRegler")}
                            </GuardedButton>
                          )}
                          {waiving !== null && waiving.id === claim.id ? (
                            <form
                              data-testid="creance-renoncer-formulaire"
                              className="flex basis-full items-center gap-2"
                              onSubmit={(event) => {
                                event.preventDefault();
                                const waive = props.waiveClaim;
                                if (waive !== undefined)
                                  actOnClaim(claim.id, () => waive(partner.id, claim.id, waiving.note));
                              }}
                            >
                              <input
                                data-testid="creance-renoncer-motif"
                                value={waiving.note}
                                placeholder={t("console.partenaires.creanceRenoncerMotif")}
                                onChange={(event) => {
                                  setWaiving({ id: claim.id, note: event.target.value });
                                }}
                                className="flex-1 rounded-lg border border-gray-200 px-2 py-1 text-xs"
                              />
                              <button
                                type="submit"
                                data-testid="creance-renoncer-confirmer"
                                disabled={claimBusy !== null || waiving.note.trim() === ""}
                                className="rounded-lg bg-indigo-600 px-2.5 py-1 text-xs font-medium text-white disabled:opacity-40"
                              >
                                {t("console.partenaires.creanceConfirmerAbandon")}
                              </button>
                            </form>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
              {claimRefusal === null ? null : (
                <p role="alert" data-testid="creance-refus" className="mt-2 text-xs text-rose-600">
                  {claimRefusal}
                </p>
              )}
            </>
          )}
        </Block>
      ) : null}

      {partner.type === "banque" ? (
        <Block title={t("console.partenaires.motifsTitre")} testId="fiche-motifs">
          {/* `CN-C.5`, WF-CON-37: the pre-filter's state read off the bank
              (the same row Priorités reads), and the adjustment by lending's
              door where the caller holds row 15's right; greyed otherwise. */}
          {bank === undefined ? (
            <p className="text-xs text-gray-500">{t("common.state.chargement")}</p>
          ) : (bank?.prefilters ?? []).length === 0 ? (
            <p data-testid="fiche-prefiltre-aucun" className="text-sm text-gray-500">
              {t("console.partenaires.prefiltreAucun")}
            </p>
          ) : (
            <ul data-testid="fiche-prefiltre" className="space-y-1">
              {(bank?.prefilters ?? []).map((one) => (
                <li key={one.criterion} className="text-sm text-gray-700">
                  {fill("console.partenaires.prefiltreAjuste", {
                    date: formatDate(new Date(one.adjustedAt)),
                    libelle: one.label,
                    seuil: String(one.thresholdPct),
                    banque: partner.name,
                  })}
                </li>
              ))}
            </ul>
          )}
          <div className="mt-2">
            {/* `D-1019-2`: a partner that is not a bank has no pre-filter to adjust, and the control is not drawn
                (it said « Bientôt disponible » in amber). A bank whose rights are missing keeps the guard. */}
            {bank === null ? null : bank === undefined ||
              props.mayAdjustPrefilter !== true ||
              props.adjustPrefilter === undefined ? (
              <GuardedButton
                disabled
                reason={t("console.partenaires.prefiltreMotifDroit")}
                variant="secondary"
                testId="fiche-ajuster-prefiltre"
              >
                {t("console.partenaires.ajusterPrefiltre")}
              </GuardedButton>
            ) : prefilterOpen ? (
              <form
                data-testid="fiche-prefiltre-form"
                className="flex flex-wrap items-end gap-2 rounded-xl bg-gray-50 px-4 py-3"
                onSubmit={(event) => {
                  event.preventDefault();
                  const door = props.adjustPrefilter;
                  if (door === undefined) return;
                  void (async () => {
                    setBusy(true);
                    setRefusal(null);
                    try {
                      await door(bank.id, { thresholdPct: Number(threshold), label: prefilterLabel.trim() });
                      setPrefilterOpen(false);
                      setEpoch((now) => now + 1);
                    } catch (failure) {
                      setRefusal(
                        (failure as { message?: string } | null)?.message ??
                          t("common.error.connexionEchouee"),
                      );
                    } finally {
                      setBusy(false);
                    }
                  })();
                }}
              >
                <label className="text-xs text-gray-600">
                  {t("console.partenaires.prefiltreSeuil")}
                  <input
                    data-testid="fiche-prefiltre-seuil"
                    inputMode="numeric"
                    value={threshold}
                    onChange={(event) => {
                      setThreshold(event.target.value);
                    }}
                    className="mt-1 block w-24 rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs"
                  />
                </label>
                <label className="min-w-0 flex-1 text-xs text-gray-600">
                  {t("console.partenaires.prefiltreLibelle")}
                  <input
                    data-testid="fiche-prefiltre-libelle"
                    value={prefilterLabel}
                    onChange={(event) => {
                      setPrefilterLabel(event.target.value);
                    }}
                    className="mt-1 block w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs"
                  />
                </label>
                <button
                  type="submit"
                  data-testid="fiche-prefiltre-enregistrer"
                  disabled={busy || threshold.trim() === "" || prefilterLabel.trim() === ""}
                  className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
                >
                  {t("console.partenaires.prefiltreEnregistrer")}
                </button>
              </form>
            ) : (
              <button
                type="button"
                data-testid="fiche-ajuster-prefiltre"
                onClick={() => {
                  setPrefilterOpen(true);
                }}
                className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                {t("console.partenaires.ajusterPrefiltre")}
              </button>
            )}
          </div>
        </Block>
      ) : null}

      <Block title={t("console.partenaires.journalTitre")} testId="fiche-journal">
        {journal === null ? (
          <p className="text-xs text-gray-500">{t("common.state.chargement")}</p>
        ) : entries.length === 0 ? (
          <p className="text-sm text-gray-500">{t("console.partenaires.journalVide")}</p>
        ) : (
          <>
            <ul className={"divide-y divide-gray-50 " + (expanded ? "max-h-80 overflow-y-auto" : "")}>
              {shown.map((entry) => {
                const channelKey = CHANNEL_WORDS.get(entry.type);
                return (
                  <li key={entry.id} data-testid={`journal-${entry.id}`} className="py-2">
                    <p className="text-sm text-gray-700">{entry.body}</p>
                    <p className="text-sm text-gray-500">
                      {channelKey === undefined ? entry.type : t(channelKey)} · {entry.authorLabel} ·{" "}
                      {formatDateTime(new Date(entry.occurredAt))}
                      {entry.isHuman ? "" : ` · ${t("console.partenaires.ligneSysteme")}`}
                    </p>
                  </li>
                );
              })}
            </ul>
            {entries.length > JOURNAL_CEILING && !expanded ? (
              <button
                type="button"
                data-testid="journal-tout"
                onClick={() => {
                  setExpanded(true);
                }}
                className="mt-2 text-xs font-medium text-indigo-600 hover:text-indigo-700"
              >
                {fill("console.partenaires.voirHistorique", { n: String(entries.length) })}
              </button>
            ) : null}
            <p data-testid="journal-echanges" className="mt-2 text-xs text-gray-500">
              {fill("console.partenaires.echangesHumains", { n: String(journal.humanExchanges) })}
            </p>
          </>
        )}
        <form
          data-testid="journal-composer"
          className="mt-3 flex flex-wrap items-end gap-2 rounded-xl bg-gray-50 px-4 py-3"
          onSubmit={(event) => {
            event.preventDefault();
            void (async () => {
              setBusy(true);
              setRefusal(null);
              try {
                await props.logExchange(partner.id, channel, body.trim());
                setBody("");
                setEpoch((now) => now + 1);
              } catch (failure) {
                setRefusal(
                  (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"),
                );
              } finally {
                setBusy(false);
              }
            })();
          }}
        >
          <label className="text-xs text-gray-600">
            {t("console.partenaires.canal")}
            <select
              data-testid="journal-canal"
              value={channel}
              onChange={(event) => {
                setChannel(event.target.value);
              }}
              className="mt-1 block rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs"
            >
              {CHANNELS.map((one) => {
                const key = CHANNEL_WORDS.get(one);
                return (
                  <option key={one} value={one}>
                    {key === undefined ? one : t(key)}
                  </option>
                );
              })}
            </select>
          </label>
          <label className="min-w-0 flex-1 text-xs text-gray-600">
            {t("console.partenaires.echange")}
            <input
              data-testid="journal-texte"
              value={body}
              onChange={(event) => {
                setBody(event.target.value);
              }}
              placeholder={t("console.partenaires.echangePlaceholder")}
              className="mt-1 block w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs"
            />
          </label>
          <button
            type="submit"
            data-testid="journal-enregistrer"
            disabled={busy || body.trim() === ""}
            className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
          >
            {t("console.partenaires.enregistrerEchange")}
          </button>
        </form>
        {refusal === null ? null : (
          <p role="alert" data-testid="fiche-refus" className="mt-2 text-sm text-rose-600">
            {refusal}
          </p>
        )}
      </Block>

      <Block title={t("console.partenaires.actionsTitre")} testId="fiche-actions">
        <p className="text-sm text-gray-500">{t("console.partenaires.actionsRond")}</p>
      </Block>

      <Block title={t("console.partenaires.contactsTitre")} testId="fiche-contacts">
        {partner.administrator === null || partner.administrator === undefined ? (
          <p className="text-sm text-gray-500">{t("console.partenaires.contactAucun")}</p>
        ) : (
          <div>
            <p className="text-sm text-gray-700">{partner.administrator.fullName}</p>
            <p className="text-xs text-gray-500">{partner.administrator.email}</p>
          </div>
        )}
        <p className="mt-1 text-xs text-gray-500">{accountLine(partner)}</p>
      </Block>

      {partner.type === "banque" && bank !== undefined && bank !== null ? (
        <BankPanelBlock
          bank={bank}
          mayAdmit={props.mayAdmitToPanel === true}
          record={props.recordPanel}
          onSaved={() => {
            setEpoch((now) => now + 1);
          }}
        />
      ) : null}

      {partner.type === "banque" && bank !== undefined && bank !== null ? (
        <BankCriteriaBlock
          bank={bank}
          mayConfigure={props.mayConfigureCriteria === true}
          record={props.recordCriteria}
          onSaved={() => {
            setEpoch((now) => now + 1);
          }}
        />
      ) : null}

      {partner.type === "concessionnaire" && props.concession !== undefined ? (
        <FicheConcession
          {...props.concession}
          concession={{ id: partner.id, nom: partner.name }}
          administrateur={partner.administrator ?? null}
        />
      ) : null}
    </div>
  );
}
